<template>
  <div>
    <v-row dense>
      <v-col
        cols="12"
        :sm="isApplyCrop ? '6' : '12'"
      >
        <GAutocomplete
          v-model="state.publicationId"
          label="Publication"
          :hint="publicationHint"
          item-value="id"
          item-text="name"
          :items="state.allPublications"
          :rules="[requiredValueRule('Publication')]"
          :menu-props="{ bottom: true, offsetY: true }"
          persistent-hint
          outlined
        >
          <template #item="{ item }">
            <div class="my-2">
              <p class="ma-0">
                {{ item.name }}
              </p>

              <p class="ma-0 text-caption grey--text text--darken-1">
                {{ item.is_internal | internalOnly }}
              </p>
            </div>
          </template>
        </GAutocomplete>
      </v-col>

      <v-col
        cols="12"
        sm="6"
      >
        <v-text-field
          v-if="isApplyCrop"
          v-model="state.crop"
          label="Crop"
          outlined
        />
      </v-col>
    </v-row>
    <v-text-field
      v-model="state.subheader"
      label="Tagline"
      counter
      :maxlength="isMarketPerspectives ? 500 : 50"
      :rules="[requiredValueRule('Tagline')]"
      outlined
    />

    <div v-if="isAgAlert && flags['todays-prices-preview']">
      <v-textarea
        :value="pricesTextareaValue"
        label="Today's Prices"
        outlined
        readonly
        auto-grow
        hide-details
        rows="4"
        :loading="pricesState === 'loading'"
        class="mt-4"
        :class="{'mb-4' : pricesState === 'loading'}"
      />

      <div
        v-if="pricesState === 'error'"
        class="d-flex justify-end mt-1 mb-4"
      >
        <v-btn
          text
          small
          color="primary"
          @click="loadCommentary"
        >
          Try Again
        </v-btn>
      </div>

      <div
        v-if="pricesState === 'loaded'"
        class="d-flex align-center mt-1 mb-4"
      >
        <v-btn
          text
          small
          color="primary"
          class="px-1"
          @click="copyCommentary"
        >
          <v-icon
            left
            small
          >
            mdi-content-copy
          </v-icon>
          Copy
        </v-btn>

        <v-spacer />

        <span class="caption grey--text text--darken-1 mr-1">Farm Market News</span>
        <v-btn
          text
          x-small
          color="primary"
          class="px-1"
          :loading="sourceEmailLoading"
          @click="viewSourceEmail"
        >
          View Email
        </v-btn>
      </div>
    </div>

    <v-dialog
      v-model="sourceEmailDialog"
      max-width="700"
      scrollable
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Source Email</span>
          <v-btn
            icon
            @click="sourceEmailDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text style="max-height: 60vh;">
          <pre style="white-space: pre-wrap; font-family: monospace; font-size: 13px;">{{ sourceEmailText }}</pre>
        </v-card-text>
      </v-card>
    </v-dialog>

    <template v-if="isMarketPerspectives">
      <v-text-field
        v-model="state.partnerName"
        label="Your Company"
        outlined
      />
      <v-file-input
        label="Company Logo"
        accept="image/png, image/jpeg, image/svg+xml"
        prepend-icon=""
        prepend-inner-icon="mdi-image"
        outlined
        hide-details="auto"
        class="mb-4"
        @change="handlePartnerLogoUpload"
      />
      <v-text-field
        v-model="state.partnerDescription"
        label="Descriptor of your company"
        outlined
      />
      <v-text-field
        v-model.number="state.displayDays"
        label="Days to display"
        type="number"
        min="1"
        hint="Number of days to show this article after publish date. Leave empty to never expire."
        persistent-hint
        outlined
        class="mb-4"
        clearable
      />
      <div class="mb-4">
        <div class="text-subtitle-2 mb-1">
          Visible to
        </div>
        <v-checkbox
          v-model="state.locations"
          value="us"
          label="United States"
          hide-details
          class="mt-0"
        />
        <v-checkbox
          v-model="state.locations"
          value="ca"
          label="Canada"
          hide-details
          class="mt-0"
        />
      </div>
    </template>

    <GAutocomplete
      v-model="state.topicIds"
      label="Topic"
      item-value="id"
      item-text="topic"
      :items="state.allTopics"
      :menu-props="{ bottom: true, offsetY: true }"
      outlined
      multiple
    />

    <GAutocomplete
      v-model="state.writerIds"
      label="Writers"
      item-value="id"
      item-text="name"
      :items="state.allWriters"
      :rules="[
        requiredValueRule('Writer'),
        requiredArrayRule('Writer'),
      ]"
      :menu-props="{ bottom: true, offsetY: true }"
      outlined
      multiple
    />

    <v-select
      v-if="!state.isPublished"
      v-model="state.isScheduled"
      label="Publication Time"
      :items="publicationTimeCategory"
      :rules="[
        requiredValueRule('Publication Time'),
      ]"
      :menu-props="{ bottom: true, offsetY: true }"
      outlined
    />

    <v-expand-transition>
      <v-row
        v-if="!state.isPublished && state.isScheduled"
        dense
      >
        <v-col cols="12">
          <v-menu
            ref="publishDatePicker"
            v-model="publishDatePicker"
            :close-on-content-click="false"
            max-width="290px"
            min-width="290px"
            offset-y
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="state.publishDate"
                label="Publish Date"
                :rules="[requiredValueRule('Publish Date')]"
                :error="Boolean(publishDateTimeError)"
                readonly
                outlined
                prepend-inner-icon="mdi-calendar"
                v-bind="attrs"
                v-on="on"
              />
            </template>

            <v-date-picker
              v-model="state.publishDate"
              color="primary"
              @change="timeWasChanged"
              @input="publishDatePicker = false"
            />
          </v-menu>
        </v-col>

        <v-col cols="12">
          <GTimePicker
            v-model="state.publishTime"
            :error="Boolean(publishDateTimeError)"
            @change="timeWasChanged"
          />
        </v-col>
        <v-col
          v-if="publishDateTimeError"
          cols="12"
          class="mt-n8 mb-4 text-subtitle-2 error--text"
        >
          {{ publishDateTimeError }}
        </v-col>
      </v-row>
    </v-expand-transition>

    <v-checkbox
      v-model="state.sendNotification"
      label="Notify members"
      class="mt-0 mb-4"
      hide-details
    />

    <div
      v-if="publication"
      class="mb-8"
    >
      <v-checkbox
        v-if="showAllowSharing"
        v-model="state.allowSharing"
        label="Allow members to share"
        class="mt-0 mb-4"
        hide-details
      />
    </div>
  </div>
</template>

<script>
import GAutocomplete from '@/components/generic/GAutocomplete.vue';
import GTimePicker from '@/components/generic/GTimePicker.vue';
import { requiredValueRule, requiredArrayRule } from '../../helpers/rules';
import {
  getLocalDateTime,
  getLocalDateTimeStringsFromISOString,
} from '../../helpers/formatting';
import { postRequest } from '@/helpers/request';
import { mapState } from 'vuex';

export default {
  components: {
    GAutocomplete,
    GTimePicker,
  },

  filters: {
    internalOnly(value) {
      return value ? 'Internal' : 'External';
    },
  },

  props: {
    state: {
      type: Object,
      default: () => ({
        isDraft: false,
      }),
    },
  },

  data() {
    return {
      isFormValid: false,

      publishDatePicker: null,
      publishTimeSetManually: false,
      publishTimeUpdater: null,

      publicationTimeCategory: [
        {
          value: false,
          text: 'Publish now',
        },
        {
          value: true,
          text: 'Schedule for later',
        },
      ],

      pricesState: 'idle',
      commentary: '',
      sourceEmailDialog: false,
      sourceEmailText: '',
      sourceEmailLoading: false,
    };
  },

  computed: {
    ...mapState('launchDarkly', ['flags']),

    publication() {
      return this.state.allPublications
        .find((x) => x.id === this.state.publicationId);
    },

    isAgAlert() {
      return this.publication?.identifier === 'ag-alert';
    },

    isMarketPerspectives() {
      return this.publication?.identifier === 'market-perspectives';
    },

    todayDate() {
      return new Date().toISOString().split('T')[0];
    },

    pricesTextareaValue() {
      if (this.pricesState === 'loading') return '';
      if (this.pricesState === 'loaded') return this.commentary;
      if (this.pricesState === 'error') return 'Commentary is not yet available for the current day.';
      return '';
    },

    isApplyCrop() {
      if (!this.state.publicationId) return false;
      return this.state.allPublications
        .find((x) => x.id === this.state.publicationId)
        .is_title_crop_specific;
    },

    publicationHint() {
      if (this.publication) return this.publication.is_internal ? 'Internal' : 'External';
      return '';
    },

    showAllowSharing() {
      return this.publication && !this.publication.is_internal;
    },

    noticeText() {
      return this.publication.is_internal
        ? 'internally.'
        : `externally with ${this.publication.user_count} members`;
    },

    publishDateTimeError() {
      const date = getLocalDateTime(this.state.publishDate, this.state.publishTime);
      const now = new Date();

      // Adding buffer of 2 minutes
      now.setMinutes(now.getMinutes() - 2, 0, 0);

      return date < now && 'Publish date & time should be in the future';
    },
  },

  watch: {
    isAgAlert: {
      immediate: true,
      handler(val) {
        if (val && this.flags['todays-prices-preview']) this.loadCommentary();
        else {
          this.pricesState = 'idle';
          this.commentary = '';
        }
      },
    },

    'publication.name': {
      handler() {
        this.state.name = this.publication ? this.publication.name : '';
      },
    },

    'state.crop': {
      handler(newVal) {
        if (newVal) {
          const crop = newVal.trim();
          const publication = (this?.publication?.name ?? '').trim();
          this.state.name = [crop, publication].join(' ').trim();
        }
      },
    },
    'state.publicationId': {
      handler() {
        this.state.crop = '';
      },
    },

    'state.isScheduled': {
      handler() {
        if (!this.state.isScheduled) this.setCurrentDateTime();
      },
    },
  },

  mounted() {
    this.autoUpdateTime();

    this.publishTimeUpdater = setInterval(() => {
      if (this.publishTimeSetManually) {
        clearInterval(this.publishTimeUpdater);
        return;
      }
      this.autoUpdateTime();
    }, 10000);
  },

  beforeDestroy() {
    clearInterval(this.publishTimeUpdater);
  },

  methods: {
    requiredValueRule,
    requiredArrayRule,

    handlePartnerLogoUpload(file) {
      if (!file) {
        this.state.partnerLogo = '';
        return;
      }
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        this.state.partnerLogo = reader.result;
      });
      reader.readAsDataURL(file);
    },

    timeWasChanged() {
      this.publishTimeSetManually = true;
    },

    autoUpdateTime() {
      if (!this.state.isScheduled && !this.state.isPublished) this.setCurrentDateTime();
    },

    setCurrentDateTime() {
      const [
        publishDate,
        publishTime,
      ] = getLocalDateTimeStringsFromISOString(new Date().toISOString());
      this.state.publishDate = publishDate;
      this.state.publishTime = publishTime;
    },

    async loadCommentary() {
      this.pricesState = 'loading';
      try {
        const data = await postRequest('/api/prices-commentary/daily/', { trading_day: this.todayDate });
        this.commentary = data.commentary ?? '';
        this.pricesState = 'loaded';
      } catch {
        this.pricesState = 'error';
      }
    },

    async copyCommentary() {
      await navigator.clipboard.writeText(this.commentary);
      this.$snackbar.success('Copied to clipboard');
    },

    async viewSourceEmail() {
      this.sourceEmailLoading = true;
      try {
        const text = await postRequest(
          '/api/prices-commentary/source/',
          { trading_day: this.todayDate },
          { responseType: 'text' },
        );
        this.sourceEmailText = text;
        this.sourceEmailDialog = true;
      } catch {
        this.$snackbar.error('Source email not available.');
      } finally {
        this.sourceEmailLoading = false;
      }
    },
  },
};
</script>
